import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, X } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { useTierState } from '../hooks/useTierState';

/**
 * TrialBanner — soft countdown banner shown above Today's Strength card on Progress.
 *
 * Visibility (single source of truth = useTierState `reason`):
 *   - admin / founder / hero / comp / subscription / subscription_grace → never (paying or lifetime, no paywall reminder needed)
 *   - app_trial → countdown variant ("N days left" / "Last day")
 *   - free → "Your trial has ended" variant
 *
 * Bug fix: previously this read `user.subscription_tier` + `profile.onboardingCompletedAt`
 * only, which meant Stripe-paid subscribers saw "Your trial has ended" copy once 14 days post-
 * signup elapsed (because the banner didn't know they had a paid subscription). Now gated on
 * the same `compute_tier_state` that powers every other paywall surface.
 *
 * Visual cost: one line, dismissable. Per Visual Minimalism Protocol.
 */

const TRIAL_DAYS = 14;
const DISMISS_KEY = 'enferno_trial_banner_dismissed_session';
const HIDDEN_REASONS = new Set(['admin', 'founder', 'hero', 'comp', 'subscription', 'subscription_grace']);

const TrialBanner = () => {
  const { profile } = useProfile();
  const { reason: tierReason, loading: tierLoading } = useTierState();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  const daysLeft = useMemo(() => {
    if (!profile.onboardingCompletedAt) return null;
    const completed = new Date(profile.onboardingCompletedAt).getTime();
    if (Number.isNaN(completed)) return null;
    const elapsedDays = (Date.now() - completed) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(TRIAL_DAYS - elapsedDays));
  }, [profile.onboardingCompletedAt]);

  if (dismissed) return null;
  // Wait for tier state before deciding — prevents a half-second flicker
  // where a paid subscriber briefly sees the banner before it disappears.
  if (tierLoading) return null;
  if (HIDDEN_REASONS.has(tierReason)) return null;
  if (!profile.completedOnboarding) return null;
  if (daysLeft === null) return null;
  if (daysLeft > TRIAL_DAYS) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const isFinalCountdown = daysLeft <= 3;
  const isExpired = daysLeft === 0;

  let label;
  if (isExpired) label = 'Your trial has ended — keep training, keep your data.';
  else if (daysLeft === 1) label = 'Last day of your trial.';
  else label = `${daysLeft} days left in your trial.`;

  return (
    <div
      className={`mb-4 rounded-lg border px-3 py-2 flex items-center justify-between gap-3 text-xs ${
        isFinalCountdown
          ? 'bg-enf-accent/10 border-enf-accent-fg/40'
          : 'bg-enf-surface-mid border-enf-surface-border'
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Flame className={`w-3.5 h-3.5 flex-shrink-0 scale-x-[-1] ${isFinalCountdown ? 'text-enf-accent-fg' : 'text-enf-text-muted'}`} />
        <span className={`truncate ${isFinalCountdown ? 'text-enf-text-primary font-semibold' : 'text-enf-text-secondary'}`}>
          {label}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          className={`px-2.5 py-1 rounded font-semibold transition-colors ${
            isFinalCountdown
              ? 'bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary'
              : 'bg-enf-surface-elevated hover:bg-enf-surface-elevated text-enf-text-primary'
          }`}
          data-testid="trial-banner-cta"
        >
          {isExpired ? 'Reactivate' : 'See plans'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1 text-enf-text-muted hover:text-enf-text-primary transition-colors"
          data-testid="trial-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default TrialBanner;
